/**
 * BBRU Console Error Handler - BUG-011
 * Suppresses known external Shopify app/platform errors
 * Preserves BBRU custom script error visibility
 * Date: 2026-05-24
 */

(function() {
  'use strict';
  
  // Error suppression patterns (external/platform issues we can't fix)
  const SUPPRESSION_PATTERNS = [
    /shopify_pay.*accelerated_checkout/i,
    /web-pixels.*pixel\.modern\.js/i,
    /web-pixel-\d+/i,
    /api-iam\.intercom\.io/i,
    /intercom.*messenger/i,
    /shop\.app.*frame-ancestors/i,
    /MIME type.*text\/html.*not executable/i,
    /Content Security Policy.*shop\.app/i,
    /intercom.*domain.*not allowed/i,
    /cdn\.shopify\.com.*web-pixels/i,
    /Mixed Content.*blocked/i,
    /analytics.*trekkie/i
  ];
  
  // Store suppressed errors for debugging (accessible via window.bbru.suppressedErrors)
  window.bbru = window.bbru || {};
  window.bbru.suppressedErrors = [];
  window.bbru.errorHandlerActive = true;
  
  // Original console methods
  const originalError = console.error;
  const originalWarn = console.warn;
  
  /**
   * Check if error message matches suppression patterns
   */
  function shouldSuppress(message) {
    const msgStr = String(message);
    return SUPPRESSION_PATTERNS.some(pattern => pattern.test(msgStr));
  }
  
  /**
   * Override console.error
   */
  console.error = function(...args) {
    const message = args.join(' ');
    
    if (shouldSuppress(message)) {
      // Store for debugging but don't display in console
      window.bbru.suppressedErrors.push({
        type: 'error',
        message: message,
        timestamp: new Date().toISOString(),
        args: args
      });
      
      // Optional: Log to a hidden debug object
      if (window.bbru.debugMode) {
        originalError.call(console, '[BBRU SUPPRESSED ERROR]', ...args);
      }
      
      return;
    }
    
    // Allow legitimate errors through (including BBRU script errors)
    originalError.apply(console, args);
  };
  
  /**
   * Override console.warn
   */
  console.warn = function(...args) {
    const message = args.join(' ');
    
    if (shouldSuppress(message)) {
      // Store for debugging but don't display in console
      window.bbru.suppressedErrors.push({
        type: 'warn',
        message: message,
        timestamp: new Date().toISOString(),
        args: args
      });
      
      if (window.bbru.debugMode) {
        originalWarn.call(console, '[BBRU SUPPRESSED WARNING]', ...args);
      }
      
      return;
    }
    
    // Allow legitimate warnings through
    originalWarn.apply(console, args);
  };
  
  /**
   * Intercept window.onerror for global error handling
   */
  const originalOnError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    const errorMsg = message + (source ? ' at ' + source : '');
    
    if (shouldSuppress(errorMsg)) {
      window.bbru.suppressedErrors.push({
        type: 'global-error',
        message: errorMsg,
        source: source,
        lineno: lineno,
        colno: colno,
        error: error,
        timestamp: new Date().toISOString()
      });
      
      // Prevent error from bubbling up
      return true;
    }
    
    // Allow legitimate errors through
    if (originalOnError) {
      return originalOnError.apply(this, arguments);
    }
    
    return false;
  };
  
  /**
   * Intercept unhandledrejection for Promise errors
   */
  window.addEventListener('unhandledrejection', function(event) {
    const reason = event.reason;
    const message = reason ? (reason.message || String(reason)) : 'Unknown rejection';
    
    if (shouldSuppress(message)) {
      window.bbru.suppressedErrors.push({
        type: 'promise-rejection',
        message: message,
        reason: reason,
        timestamp: new Date().toISOString()
      });
      
      // Prevent error from showing in console
      event.preventDefault();
    }
  });
  
  /**
   * Network error suppression (for 404s and MIME errors)
   */
  if (window.PerformanceObserver) {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'resource') {
            const resource = entry.name;
            
            // Check for known problematic resources
            if (shouldSuppress(resource)) {
              // Resource loaded (or failed), but we're suppressing related errors
              // Note: Can't actually prevent network requests, but errors are suppressed
            }
          }
        }
      });
      
      observer.observe({ entryTypes: ['resource'] });
    } catch (e) {
      // PerformanceObserver not supported, silently fail
    }
  }
  
  // Initialize message
  console.log('%c[BBRU] Console error handler initialized', 
    'color: #112146; font-weight: bold;');
  console.log('%c[BBRU] External platform errors are being suppressed', 
    'color: #666; font-style: italic;');
  console.log('%c[BBRU] View suppressed errors: window.bbru.suppressedErrors', 
    'color: #666; font-style: italic;');
  console.log('%c[BBRU] Enable debug mode: window.bbru.debugMode = true', 
    'color: #666; font-style: italic;');
  
  // Export utility methods
  window.bbru.clearSuppressedErrors = function() {
    window.bbru.suppressedErrors = [];
    console.log('[BBRU] Suppressed errors cleared');
  };
  
  window.bbru.getSuppressedErrorCount = function() {
    return window.bbru.suppressedErrors.length;
  };
  
  window.bbru.getSuppressedErrorsByType = function(type) {
    return window.bbru.suppressedErrors.filter(err => err.type === type);
  };
  
})();
